import { Link } from 'react-router-dom';
import { useReveal } from '../hooks/useReveal.js';
import { BookIcon, CandleIcon, CenserIcon, QuillIcon } from '../components/icons.js';
import { PAYPAL_LINK } from '@/lib/giving.js';

const keeps = [
  { icon: CandleIcon, title: 'The servers', text: 'That answer every morning with the day.' },
  { icon: CenserIcon, title: 'The voices', text: 'Narration and chant behind every prayer.' },
  {
    icon: BookIcon,
    title: 'The texts',
    text: 'Prayer books and Scripture, licensed and proofread.',
  },
  { icon: QuillIcon, title: 'The time', text: 'Of the small team that builds and keeps it.' },
];

/**
 * A place to offer a gift toward the keeping of the app. Deliberately
 * light: four things a gift keeps, one gold door to PayPal's payment page,
 * and a path to /support for the fuller story.
 */
export function Support() {
  const headRef = useReveal<HTMLDivElement>(0.25);
  const gridRef = useReveal<HTMLDivElement>(0.2);
  const ctaRef = useReveal<HTMLDivElement>(0.3);

  return (
    <section
      id="support"
      className="relative w-full overflow-hidden"
      style={{ padding: '84px 20px', background: 'var(--ow-bg-subtle)' }}
    >
      {/* a low candle-glow behind the heading */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          width: 720,
          height: 720,
          left: '50%',
          top: -260,
          transform: 'translateX(-50%)',
          background: 'radial-gradient(closest-side, var(--ow-gold-glow), transparent 70%)',
        }}
      />

      <div className="relative mx-auto" style={{ maxWidth: 920 }}>
        <div ref={headRef} className="ow-reveal text-center">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § IX - The Lamp
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.1,
            }}
          >
            Keep the lamp lit.
          </h2>
          <p
            className="scripture text-secondary mt-5 mx-auto"
            style={{ fontSize: 21, lineHeight: 1.55, maxWidth: 600 }}
          >
            Orthodox Way is free and carries no advertising. It is kept by a few, for everyone. If
            it has served your prayer, you may help carry it.
          </p>
        </div>

        <div ref={gridRef} className="ow-reveal mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {keeps.map((k, i) => (
            <div
              key={k.title}
              className="ow-cap-card bg-bg-elevated border border-border rounded-card text-center"
              style={
                {
                  padding: '26px 18px',
                  ['--reveal-delay' as never]: `${i * 90}ms`,
                } as React.CSSProperties
              }
            >
              <span
                className="inline-flex items-center justify-center text-gold"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: '1px solid var(--ow-gold)',
                  boxShadow: '0 0 0 4px var(--ow-gold-glow)',
                }}
              >
                <k.icon size={26} />
              </span>
              <h3
                className="font-display text-primary mt-5"
                style={{ fontSize: 18, fontWeight: 500, letterSpacing: '0.02em' }}
              >
                {k.title}
              </h3>
              <p className="text-secondary mt-2" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
                {k.text}
              </p>
            </div>
          ))}
        </div>

        <div ref={ctaRef} className="ow-reveal mt-12 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={PAYPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="ow-cta-primary"
            >
              Offer a gift
            </a>
            <Link to="/support" className="ow-cta-ghost">
              Where a gift goes
            </Link>
          </div>
          <p
            className="text-muted mt-6 mx-auto"
            style={{ fontSize: 12.5, lineHeight: 1.7, maxWidth: 520 }}
          >
            Any amount, once, through PayPal: from a PayPal balance or a card from any country.
            Nothing is withheld from those who cannot.
          </p>
        </div>
      </div>
    </section>
  );
}
